import { BreakpointObserver } from "@angular/cdk/layout";
import { Component, inject, Input, OnInit } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ConfirmModalComponent } from "@components/confirm-modal/confirm-modal.component";
import { FilesOverlayComponent } from "@components/files-overlay/files-overlay.component";
import { ProjectDetailFileCardComponent } from "@components/project-cards/project-detail-file-card/project-detail-file-card.component";
import { ProjectDetailMobileFileCardComponent } from "@components/project-cards/project-detail-mobile-file-card/project-detail-mobile-file-card.component";
import { ProjectFile } from "@domain/project/project-file";
import type { ScopedProject } from "@domain/project/project-guards";
import { ProjectService } from "@domain/project/project.service";
import { UserService } from "@domain/user/user.service";
import { InlineMessageComponent } from "@odx/angular/components/inline-message";
import { ModalOptions, ModalService } from "@odx/angular/components/modal";
import { filter, map, switchMap } from "rxjs";
import { Breakpoints } from "../../../../styles/breakpoints";

@Component({
  selector: "app-files",
  imports: [FilesOverlayComponent, ProjectDetailFileCardComponent, ProjectDetailMobileFileCardComponent, InlineMessageComponent],
  templateUrl: "./files.component.html",
  styleUrl: "./files.component.scss",
  standalone: true,
})
export class FilesComponent implements OnInit {
  private breakpointObserver = inject(BreakpointObserver);
  private projectService = inject(ProjectService);
  private userService = inject(UserService);

  @Input() set projectData(project: ScopedProject) {
    this.project = project;
    this.files = [];
    this.expandedFileId = undefined;
  }

  private readonly modalService = inject(ModalService);

  project!: ScopedProject;
  files: ProjectFile[] = [];
  protected expandedFileId?: string;

  protected readonly MAX_FILE_SIZE = 50 * 1024 * 1024;
  protected readonly TOTAL_FILE_SIZE = Math.pow(2, 30);
  protected isMobile = toSignal(this.breakpointObserver.observe(Breakpoints.mobile).pipe(map((result) => result.matches)), {
    initialValue: false,
  });
  protected isReadOnlyUser = toSignal(this.userService.user$.pipe(map((user) => user.readOnlyAccess)), { initialValue: false });
  protected hasWritePermission = toSignal(
    this.projectService.hasUserWritePermission$.pipe(filter((val): val is boolean => val !== undefined)),
    { initialValue: false },
  );

  ngOnInit(): void {
    if (this.project.cloudId && navigator.onLine) {
      this.refreshProjectFiles(this.project.cloudId).subscribe();
    }
  }

  onFileUploaded(blobs: Blob[]) {
    this.uploadFiles(blobs)
      .pipe(
        switchMap((metadata) => {
          return this.projectService.updateLocalMetadata(metadata);
        }),
        map(() => {
          if (this.project.cloudId) {
            return this.refreshProjectFiles(this.project.cloudId).subscribe();
          }
          return undefined;
        }),
      )
      .subscribe();
  }

  onDownload(file: ProjectFile) {
    if (this.project.cloudId && file.id) {
      this.downloadFile(file);
    }
  }

  onDelete(file: ProjectFile) {
    if (this.project.cloudId && file.id) {
      this.deleteFile(this.project.cloudId, file.id);
    }
  }

  toggleFileExpansion(file: ProjectFile) {
    const fileId = this.getFileId(file);
    this.expandedFileId = this.expandedFileId === fileId ? undefined : fileId;
  }

  private deleteFile(cloudId: string, fileId: string) {
    const options: Partial<ModalOptions> = {
      id: this.isMobile() ? "project-files-confirm-modal" : undefined,
      data: {
        text: $localize`:@@modal.deleteFile:Wollen Sie das Dokument wirklich löschen?`,
      },
    };
    this.modalService
      .open(ConfirmModalComponent, options)
      .onClose$.pipe(
        switchMap(() =>
          this.projectService
            .removeFile(cloudId, fileId)
            .pipe(
              switchMap((metadata) =>
                this.projectService
                  .updateLocalMetadata(metadata)
                  .pipe(switchMap(() => (this.project.cloudId ? this.refreshProjectFiles(this.project.cloudId) : []))),
              ),
            ),
        ),
      )
      .subscribe();
  }

  private downloadFile(file: ProjectFile) {
    return this.projectService.downloadFile(file);
  }

  private uploadFiles(blobs: Blob[]) {
    return this.projectService.addFiles(blobs, this.project.cloudId);
  }

  private refreshProjectFiles(cloudId: string) {
    return this.projectService.loadFiles(cloudId).pipe(
      map((response) => {
        this.files = response;
        if (this.expandedFileId && !response.some((file) => this.getFileId(file) === this.expandedFileId)) {
          this.expandedFileId = undefined;
        }
        return this.files;
      }),
    );
  }

  protected getFileId(file: ProjectFile) {
    return file.id ?? `${file.name}-${file.type}-${file.size}-${String(file.uploadedAt)}`;
  }

  protected readonly Math = Math;
  protected readonly navigator = navigator;
}
